import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';

import utils = egr.wcf.utils;
import { Bouncer } from './Bouncer';

export class ActionTimeoutError extends utils.CustomError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, ActionTimeoutError.prototype);
    }
}

export interface WaitForActionArguments {
    actionTypes: Array<ActionConstants>;
    timeout?: number;
    condition?: (action: DispatcherBaseAction) => boolean;
    rejectActionTypes?: Array<ActionConstants>;
    rejectError?: Error;
}

export abstract class ActionHelper {
    public static waitFor(args: WaitForActionArguments): Promise<DispatcherBaseAction> {
        return new Promise<DispatcherBaseAction>((resolve: (action: DispatcherBaseAction) => void, reject: (error: Error | null) => void): void => {
            let timeoutId: number;
            let cleanup: () => void;

            if (args.timeout != null) {
                const error: Error = args.rejectError != null ? args.rejectError : new ActionTimeoutError('Action timeout reached');
                timeoutId = window.setTimeout(
                    (): void => {
                        cleanup();
                        reject(error);
                    },
                    args.timeout
                );
            }

            const dispatcherToken: string = AppDispatcher.register((action: DispatcherBaseAction): void => {
                if (args.actionTypes.indexOf(action.actionType) !== -1 && (args.condition == null || args.condition(action))) {
                    cleanup();
                    // ensure that the action processing finished
                    setTimeout(
                        (): void => {
                            resolve(action);
                        },
                        0
                    );
                } else if (args.rejectActionTypes != null && args.rejectActionTypes.indexOf(action.actionType) !== -1) {
                    cleanup();
                    // ensure that the action processing finished
                    setTimeout(
                        (): void => {
                            reject(null);
                        },
                        0
                    );
                }
            });

            cleanup = (): void => {
                clearTimeout(timeoutId);
                AppDispatcher.unregister(dispatcherToken);
            };
        });
    }

    public static getPausingAction<T>(spareTime: number, callback: () => Promise<T>): () => Promise<T | null> {
        let needsRest: boolean = false;
        const bouncer: Bouncer<T> = new Bouncer(callback, true);

        let actifity: () => Promise<T | null>;
        const freshenUp: () => void = (): void => {
            needsRest = false;
            if (bouncer.getQueueLength() !== 0) {
                actifity();
            }
        };

        actifity = async (): Promise<T | null> => {
            if (needsRest) {
                return bouncer.request();
            }

            needsRest = true;

            try {
                return await bouncer.run();
            } finally {
                setTimeout(freshenUp, spareTime);
            }
        };

        return actifity;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/ActionHelper.ts